import { createSelector } from '@reduxjs/toolkit';
import { extInfo } from 'lib/extInfo';
import { sort } from 'ramda';
import { CAMERA_UPLOADS_FOLDER_ID, DEFAULT_FILES_HASHES, ROOT_FOLDER_ID } from 'reactApp/constants/magicIdentificators';
import { isItemPreviewableImageWithThumbs, isItemPreviewableVideoWithThumbs } from 'reactApp/modules/file/utils';
import { FilesState, HomeFile, HomeFolder, HomeItem, MediaStatistics } from 'reactApp/modules/home/home.types';
import { getCurrentRouteId, getCurrentStorage } from 'reactApp/modules/router/router.selectors';
import { getSharedIds } from 'reactApp/modules/shared/shared.selectors';
import { intlStringCompare } from 'reactApp/modules/sort/sort.helpers';
import { isDomainFolder, isMountedFolder, isSharedFolder } from 'reactApp/modules/storage/storage.selectors';
import { EStorageType } from 'reactApp/modules/storage/storage.types';
import { RootState } from 'reactApp/store';
import { Kind } from 'reactApp/types/Tree';
import { BreadcrumbsListItem } from 'reactApp/ui/BreadcrumbsContainer/BreadcrumbsContainer';
import { renameCameraUploadFolder } from 'reactApp/utils/tree.helpers';

const getFilesState = (state: RootState): FilesState => state.home;
const getId = (state, id) => id;

export const getHomeList = createSelector(getFilesState, (state) => state.list);

export const getHomeItemById = createSelector(
    getFilesState,
    getId,
    (filesState, id): HomeFolder | HomeFile | undefined => filesState.list[id]
);

export const getHomeStatistics = createSelector(getFilesState, (filesState) => filesState.homeStatistics);
export const getCameraUploadsStatistics = createSelector(getFilesState, (filesState) => filesState.cameraUploadsStatistics);

export const getFolderListById = createSelector(
    getHomeItemById,
    (state: RootState) => state,
    (folder, state) => {
        if (folder?.isFolder && Array.isArray(folder.childs)) {
            return sort(
                (a, b) => (a && b ? intlStringCompare(a.name, b.name) : 0),
                folder.childs.map((child) => getHomeItemById(state, child))
            );
        }
        return [];
    }
);

export const getCurrentFolderHome = createSelector(
    getCurrentRouteId,
    (state) => state,
    (routeId, state): HomeFolder | undefined => {
        if (!routeId) {
            return;
        }

        const item = getHomeItemById(state, routeId);

        if (!item) {
            return;
        }

        if (item.isFolder) {
            return item;
        }

        return getHomeItemById(state, item.parent) as HomeFolder;
    }
);

export const getCurrentChildIds = createSelector(getCurrentFolderHome, (folder): string[] => {
    if (folder) {
        return folder.childs;
    }

    return [];
});

export const isReadOnly = createSelector(
    getFilesState,
    (state, id): string => id,
    (state, id): boolean => {
        let folder = state.list[id];
        while (folder && folder.isFolder && !folder.isReadOnly && folder.parent) {
            folder = state.list[folder.parent];
        }

        return folder?.isFolder ? Boolean(folder.isReadOnly) : false;
    }
);

export const isThisOrParentMounted = createSelector(getHomeItemById, getHomeList, (item, list) => {
    if (!item) {
        return false;
    }

    if (item.isMounted) {
        return true;
    }

    if (item.isFolder && (isMountedFolder(item) || isDomainFolder(item))) {
        return true;
    }

    let result = false;

    let parent = list[item.parent || ''] as HomeFolder;

    while (parent) {
        if (isMountedFolder(parent) || isDomainFolder(parent)) {
            result = true;
            break;
        }

        parent = list[parent.parent || ''] as HomeFolder;
    }

    return result;
});

export const isDefaultRootContent = createSelector(
    (state: RootState) => getFolderListById(state, '/'),
    (state: RootState) => getHomeItemById(state, '/'),
    (state: RootState) => getCurrentStorage(state),
    (list, folder, storage) => {
        if (storage !== EStorageType.home) {
            return false;
        }

        return (
            (list && list.length && list.every((item) => item && !item.isFolder && DEFAULT_FILES_HASHES.includes(item.hash || ''))) ||
            (list && list.length === 0 && (folder as HomeFolder)?.isLoaded)
        );
    }
);

export const getMediaFromFolder = createSelector(
    (state, sortByTime): boolean => sortByTime,
    (state, sortByTime, number: number | null): number | null => number,
    (state, sortByTime, number: number | null, folderId: string): string => folderId,
    (state, sortByTime, number: number | null, folderId: string, onlyPhotos: boolean): boolean => onlyPhotos,
    (state) => state,
    (sortByTime, number, folderId, onlyPhotos, state): any[] => {
        let list = getFolderListById(state, folderId) as HomeItem[];
        if (!Array.isArray(list)) {
            return [];
        }

        const photoExt = extInfo.getPhotoExts();

        list = list.filter((item) => {
            const fileItem = item as HomeFile;
            if (!fileItem) {
                return false;
            }
            return (
                (isItemPreviewableImageWithThumbs(fileItem) && (!onlyPhotos || photoExt.includes(fileItem.ext?.toLowerCase()))) ||
                (!onlyPhotos && isItemPreviewableVideoWithThumbs(fileItem))
            );
        }) as HomeFile[];

        if (sortByTime) {
            list = ([...list] as HomeFile[]).sort((item1, item2) => item2?.mtime - item1?.mtime);
        }

        if (number && number > 0) {
            list = list.splice(0, number);
        }

        return list;
    }
);

export const getCameraUploadsMedia = createSelector(
    (state, sortByTime): boolean => sortByTime,
    (state, sortByTime, number: number | null): number | null => number,
    (state) => state,
    (sortByTime, number, state): any[] => getMediaFromFolder(state, sortByTime, number, CAMERA_UPLOADS_FOLDER_ID, true)
);

export const getCameraUploadsStatisticsPrev = createSelector(
    getSharedIds,
    (state) => state,
    (idxs, state) => {
        const list = getFolderListById(state, CAMERA_UPLOADS_FOLDER_ID) as HomeItem[];

        let photos = 0;
        let videos = 0;

        (Array.isArray(list) ? list : []).forEach((item) => {
            const fileItem = item as HomeFile;
            if (!fileItem) {
                return;
            }
            if (fileItem?.kind === 'image') {
                photos++;
            }
            if (fileItem?.kind === 'video') {
                videos++;
            }
        });

        return {
            photos,
            videos,
        } as MediaStatistics;
    }
);

export const getRootFolderList = createSelector(getHomeList, (home) => home[ROOT_FOLDER_ID]);

export const getHomeBreadcrumbs = createSelector(
    getCurrentFolderHome,
    (state) => state,
    (currentFolder, state): BreadcrumbsListItem[] => {
        if (!currentFolder) {
            return [];
        }

        const breadCrumbs: BreadcrumbsListItem[] = [
            {
                name: 'Все файлы',
                id: ROOT_FOLDER_ID,
                kind: 'folder' as Kind,
                storage: EStorageType.home,
            },
        ];

        const pathParts = currentFolder.home.split('/').filter(Boolean);
        const isRoot = currentFolder.id === ROOT_FOLDER_ID;
        let isPrevShared = false;

        pathParts.reduce((fullPath, path) => {
            const currentPath = `${fullPath}/${path}`;
            // Для вложенных папок монтированной папки приходит isMounted, но kind - не mounted
            const isPrevMounted = currentFolder.isMounted && !isMountedFolder(currentFolder);

            breadCrumbs.push({
                name: renameCameraUploadFolder({ name: path }),
                id: currentPath,
                kind: currentFolder.kind,
                storage: EStorageType.home,
                weblink: currentFolder.weblink,
                showWeblink: !isRoot,
                showShare: !isRoot && !isPrevShared && !isPrevMounted && !isDomainFolder(currentFolder),
            });

            const item = getHomeItemById(state, currentPath);
            isPrevShared = isPrevShared || isSharedFolder(item);

            return currentPath;
        }, '');

        return breadCrumbs;
    }
);
